import { c as _c } from "react/compiler-runtime";
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import IconButton from '@classify/react-core-components/IconButton';
import MailOutline from '@mui/icons-material/MailOutline';
import Close from '@mui/icons-material/Close';
import { styled } from '@classify/react-core-components/styles';
import { useGet } from '@openclassrooms/data-connect-web/hooks';
import { translate } from '@openclassrooms/translations';
import { consumeMessagingResume } from './messagingNavigation';
const MessagingBoard = lazy(() => import('./MessagingBoard'));
const MessagingModal = styled(Dialog)(({
  theme: {
    breakpoints,
    spacing,
    zIndex
  }
}) => ({
  '& .MuiDialog-container': {
    alignItems: 'flex-end',
    justifyContent: 'flex-end'
  },
  '& .MuiDialog-paper': {
    width: '100%',
    height: 'min(720px, calc(100dvh - 64px))',
    maxHeight: 'calc(100dvh - 64px)',
    maxWidth: '100%',
    margin: 0,
    borderRadius: '8px 8px 0 0',
    [breakpoints.up('md')]: {
      width: 640,
      marginRight: spacing(3)
    }
  },
  '&[data-expanded="true"]': {
    zIndex: zIndex.appBar - 1,
    pointerEvents: 'none',
    '& .MuiDialog-container': {
      alignItems: 'flex-end',
      justifyContent: 'stretch'
    },
    '& .MuiDialog-paper': {
      pointerEvents: 'auto',
      width: '100%',
      height: 'calc(100dvh - var(--messages-top, 64px))',
      maxHeight: 'calc(100dvh - var(--messages-top, 64px))',
      margin: 0,
      borderRadius: 0
    }
  }
}));
const MessageTrigger = styled('span')({
  display: 'inline-flex',
  position: 'relative',
  flexShrink: 0
});
const UnreadBadge = styled('span')(({
  theme: {
    palette
  }
}) => ({
  position: 'absolute',
  top: -2,
  right: -2,
  pointerEvents: 'none',
  textAlign: 'center',
  minWidth: 16,
  padding: '0 3px',
  borderRadius: 10,
  background: palette.primary.main,
  color: palette.primary.contrastText,
  fontSize: 10,
  lineHeight: '16px'
}));
export default function MessagingButton(t0) {
  const $ = _c(29);
  const {
    unreadCount: initialUnreadCount
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryParams: {
        limit: 1
      },
      refetchInterval: 30000
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const counter = useGet("/me/private-conversations", t1);
  const unreadCount = counter.data?.body.unreadCount ?? initialUnreadCount;
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [top, setTop] = useState(64);
  const trigger = useRef(null);
  let t2;
  let t3;
  if ($[1] !== isExpanded) {
    t2 = () => {
      if (!isExpanded) {
        return;
      }
      const updateTop = () => {
        setTop(trigger.current?.closest("header")?.getBoundingClientRect().bottom ?? 64);
      };
      window.addEventListener("resize", updateTop);
      window.addEventListener("scroll", updateTop, true);
      return () => {
        window.removeEventListener("resize", updateTop);
        window.removeEventListener("scroll", updateTop, true);
      };
    };
    t3 = [isExpanded];
    $[1] = isExpanded;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const resumeId = consumeMessagingResume();
      if (resumeId === undefined) {
        return;
      }
      setConversationId(resumeId);
      setHasOpened(true);
      setIsOpen(true);
    };
    t5 = [];
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] !== unreadCount) {
    t6 = translate("messaging.tab.messages", {
      count: unreadCount
    });
    $[6] = unreadCount;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== unreadCount) {
    t7 = translate("messaging.tab.messages", {
      count: unreadCount
    });
    $[8] = unreadCount;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setHasOpened(true);
      setIsOpen(true);
    };
    t9 = <MailOutline />;
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t6 || $[13] !== t7) {
    t10 = <IconButton tooltip={t6} aria-label={t7} onClick={t8}>{t9}</IconButton>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== unreadCount) {
    t11 = unreadCount > 0 && <UnreadBadge aria-hidden="true">{unreadCount > 99 ? "99+" : unreadCount}</UnreadBadge>;
    $[15] = unreadCount;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t10 || $[18] !== t11) {
    t12 = <MessageTrigger ref={trigger}>{t10}{t11}</MessageTrigger>;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== conversationId || $[21] !== hasOpened || $[22] !== isExpanded || $[23] !== isOpen || $[24] !== top) {
    t13 = hasOpened && <MessagingModal data-expanded={isExpanded} sx={{
      "--messages-top": `${top}px`
    }} open={isOpen} keepMounted={true} maxWidth={false} hideBackdrop={isExpanded} disableEnforceFocus={isExpanded} disableScrollLock={isExpanded} PaperProps={{
      role: isExpanded ? "region" : "dialog",
      "aria-modal": !isExpanded,
      "aria-label": translate("messaging.title")
    }} onClose={() => setIsOpen(false)}><Suspense fallback={<><IconButton tooltip={translate("messaging.close.label")} aria-label={translate("messaging.close.label")} onClick={() => setIsOpen(false)}><Close /></IconButton><p role="status">{translate("messaging.loading")}</p></>}><MessagingBoard conversationId={conversationId} onSelect={setConversationId} isActive={isOpen} isCompact={!isExpanded} onClose={() => setIsOpen(false)} onExpand={isExpanded ? undefined : () => {
          setTop(trigger.current?.closest("header")?.getBoundingClientRect().bottom ?? 64);
          setIsExpanded(true);
        }} onReduce={isExpanded ? () => setIsExpanded(false) : undefined} /></Suspense></MessagingModal>;
    $[20] = conversationId;
    $[21] = hasOpened;
    $[22] = isExpanded;
    $[23] = isOpen;
    $[24] = top;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t12 || $[27] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return t14;
}